import { useState } from "react"
import { Navigate, useLocation, useParams } from "react-router-dom"
import { FormikHelpers } from "formik"
import { useToast } from "~/utils/Toast"
import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"

import { apiSponsorships, apiSponsorshipAreas } from "~/routes"
import PageContainer from "~/library/PageContainer"
import LoadingWrapper from "~/library/LoadingWrapper"
import SponsorshipForm from "~/components/Sponsorship/SponsorshipForm"
import Sponsorship from "~/types/Sponsorship"
import { Area } from "~/types/sponsorship/Area"

const EditSponsorship = () => {
  const { venueSlug } = getAppConfig()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const toast = useToast()

  const [isSponsorshipUpdated, setIsSponsorshipUpdated] = useState<boolean>(false)

  const { data: sponsorship, isLoading } = useFetchResource<
    { sponsorship: Sponsorship },
    "sponsorship"
  >(apiSponsorships.show.path({ venueSlug, id: id! }), "sponsorship", {})

  const filterAreaType = location.state?.areaType || sponsorship?.area?.areaType

  const areasParams = {
    "q[s]": "area_type desc",
    ...(filterAreaType && { "q[area_type_eq]": filterAreaType }),
  }

  const { data: areas, isLoading: isLoadingAreas } = useFetchResource<
    { sponsorshipAreas: Area[] },
    "sponsorshipAreas"
  >(apiSponsorshipAreas.index.path({ venueSlug }), "sponsorshipAreas", areasParams)

  const handleSubmit = (formValues: any, helpers: FormikHelpers<any>) => {
    const data = {
      venue_slug: venueSlug,
      id: id!,
      sponsorship: {
        startOn: formValues.startOn,
        endOn: formValues.endOn,
        areaId: formValues.areaId,
        companyId: formValues.companyId,
        priceCents: parseInt(formValues.priceCents),
        quantity: formValues.quantity ? parseInt(formValues.quantity) : null,
        areaType: sponsorship?.area?.areaType,
        ...(formValues.companyAttributes.name && {
          companyAttributes: formValues.companyAttributes,
        }),
      },
    }

    const req = apiSponsorships.update({ data })
    req
      .then(() => {
        setIsSponsorshipUpdated(true)
        toast("Sponsorship updated successfully", "success")
      })
      .catch((res) => {
        toast(`${res.body?.errors?.join(", ") || "Please try again"}`, "danger")
      })
      .finally(() => {
        helpers.setSubmitting(false)
      })
  }

  if (isSponsorshipUpdated) {
    return <Navigate to={`/sponsorship/${id}`} />
  }

  return (
    <PageContainer>
      <div className="row h-100 justify-content-center">
        <div style={{ width: "800px" }} className="align-center mt-4">
          <h3 className="mb-3">Edit Sponsorship</h3>
          <LoadingWrapper isLoading={isLoading}>
            <SponsorshipForm
              onSubmit={handleSubmit}
              initialValues={sponsorship}
              isEditing={true}
              areas={areas || []}
              isLoadingAreas={isLoadingAreas}
              onCancel={() => window.history.back()}
            />
          </LoadingWrapper>
        </div>
      </div>
    </PageContainer>
  )
}

export default EditSponsorship
